import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  DatePicker,
  Button,
  Row,
  Col,
  Card,
  Select,
  Typography,
  Rate,
  Space,
  message,
  Spin
} from 'antd';
import {
  SaveOutlined,
  SendOutlined,
  ArrowLeftOutlined
} from "@ant-design/icons";
import * as actions from "store/actions/AuthActions";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from 'dayjs';

const { TextArea } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

const PerformanceReviewForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [reviewers, setReviewers] = useState([]);
  const [reviewData, setReviewData] = useState(null);

  // Rating descriptions
  const ratingDescriptions = ['Needs Improvement', 'Below Expectations', 'Meets Expectations', 'Exceeds Expectations', 'Outstanding'];

  // Load employees for dropdowns
  const loadEmployees = async () => {
    try {
      const response = await actions.loadEmployees(1, 1000);
      if (response && response.results) {
        setEmployees(response.results);
        setReviewers(response.results);
      }
    } catch (error) {
      console.error("Failed to load employees", error);
      message.error("Failed to load employees");
    }
  };

  // Load existing review for editing
  const loadReview = async () => {
    if (id) {
      setLoading(true);
      try {
        const response = await actions.fetchEmployeeReview(id);
        if (response) {
          setReviewData(response);
          const formData = { ...response };

          // Convert date fields
          if (formData.review_period_start) {
            formData.review_period_start = dayjs(formData.review_period_start);
          }
          if (formData.review_period_end) {
            formData.review_period_end = dayjs(formData.review_period_end);
          }

          form.setFieldsValue(formData);
        }
      } catch (error) {
        console.error("Failed to load review", error);
        message.error("Failed to load review");
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmployees();
    if (id) {
      loadReview();
    }
  }, [id]);

  // Save review
  const saveReview = async (values, submitAfterSave = false) => {
    setSaving(true);

    // Format dates
    const formattedValues = { ...values };
    if (formattedValues.review_period_start) {
      formattedValues.review_period_start = dayjs(formattedValues.review_period_start).format('YYYY-MM-DD');
    }
    if (formattedValues.review_period_end) {
      formattedValues.review_period_end = dayjs(formattedValues.review_period_end).format('YYYY-MM-DD');
    }

    try {
      let response;
      if (id) {
        response = await actions.updateEmployeeReview(id, formattedValues);
        if (response) {
          message.success("Review updated successfully");
        }
      } else {
        response = await actions.createEmployeeReview(formattedValues);
        if (response) {
          message.success("Review created successfully");

          // If we want to submit after creating, do it now
          if (submitAfterSave && response.id) {
            await actions.submitReview(response.id);
            message.success("Review submitted successfully");
          }
        }
      }

      // If submitAfterSave and editing existing, submit it
      if (submitAfterSave && id) {
        await actions.submitReview(id);
        message.success("Review submitted successfully");
      }

      navigate('/hr/performance-reviews');
    } catch (error) {
      console.error("Failed to save review", error);
      message.error("Failed to save review");
    }
    setSaving(false);
  };

  // Handle form submit
  const handleSave = () => {
    form.validateFields().then(values => {
      saveReview(values, false);
    }).catch(error => {
      console.error("Validation failed", error);
    });
  };

  // Handle save and submit
  const handleSaveAndSubmit = () => {
    form.validateFields().then(values => {
      saveReview(values, true);
    }).catch(error => {
      console.error("Validation failed", error);
    });
  };

  // Check if review can be edited
  const isEditable = !id || (reviewData && reviewData.status === 'DRAFT');

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
        <Col>
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/hr/performance-reviews')}
            >
              Back
            </Button>
            <Title level={4} style={{ margin: 0 }}>
              {id ? 'Edit Performance Review' : 'Create Performance Review'}
            </Title>
          </Space>
        </Col>
        <Col>
          <Space>
            <Button
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
              disabled={!isEditable}
            >
              Save as Draft
            </Button>
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSaveAndSubmit}
              loading={saving}
              disabled={!isEditable}
            >
              Save & Submit
            </Button>
          </Space>
        </Col>
      </Row>

      {!isEditable && (
        <Card style={{ marginBottom: '24px', backgroundColor: '#fff7e6' }}>
          <Text type="warning">
            This review has been submitted and cannot be edited. Only draft reviews can be modified.
          </Text>
        </Card>
      )}

      <Form
        form={form}
        layout="vertical"
        disabled={!isEditable}
        initialValues={{
          review_type: 'MONTHLY',
          overall_rating: 3,
          performance_rating: 3,
          communication_rating: 3,
          teamwork_rating: 3,
          punctuality_rating: 3
        }}
      >
        {/* Basic Information */}
        <Card title="Basic Information" style={{ marginBottom: '24px' }}>
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item
                name="employee"
                label="Employee"
                rules={[{ required: true, message: 'Please select an employee' }]}
              >
                <Select
                  placeholder="Select Employee"
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option?.children?.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {employees.map(emp => (
                    <Option key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="reviewer"
                label="Reviewer"
                rules={[{ required: true, message: 'Please select a reviewer' }]}
              >
                <Select
                  placeholder="Select Reviewer"
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option?.children?.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {reviewers.map(emp => (
                    <Option key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="review_type"
                label="Review Type"
                rules={[{ required: true, message: 'Please select review type' }]}
              >
                <Select placeholder="Select Review Type">
                  <Option value="MONTHLY">Monthly Review</Option>
                  <Option value="CUSTOM">Custom Review</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="title"
                label="Review Title (Optional)"
              >
                <Input placeholder="e.g., Q1 2024 Performance Review" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="review_period_start"
                label="Review Period Start"
                rules={[{ required: true, message: 'Please select start date' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="review_period_end"
                label="Review Period End"
                rules={[{ required: true, message: 'Please select end date' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Ratings */}
        <Card title="Ratings" style={{ marginBottom: '24px' }}>
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item
                name="overall_rating"
                label="Overall Rating"
                rules={[{ required: true, message: 'Please provide overall rating' }]}
              >
                <Rate
                  tooltips={ratingDescriptions}
                  style={{ fontSize: '24px' }}
                />
              </Form.Item>
              <Text type="secondary">
                {form.getFieldValue('overall_rating') && ratingDescriptions[form.getFieldValue('overall_rating') - 1]}
              </Text>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="performance_rating"
                label="Performance Rating"
              >
                <Rate tooltips={ratingDescriptions} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="communication_rating"
                label="Communication"
              >
                <Rate tooltips={ratingDescriptions} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="teamwork_rating"
                label="Teamwork"
              >
                <Rate tooltips={ratingDescriptions} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="punctuality_rating"
                label="Punctuality"
              >
                <Rate tooltips={ratingDescriptions} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Feedback */}
        <Card title="Feedback" style={{ marginBottom: '24px' }}>
          <Row gutter={24}>
            <Col xs={24}>
              <Form.Item
                name="strengths"
                label="Strengths"
              >
                <TextArea
                  rows={4}
                  placeholder="Describe the employee's key strengths and accomplishments..."
                  maxLength={2000}
                  showCount
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                name="areas_for_improvement"
                label="Areas for Improvement"
              >
                <TextArea
                  rows={4}
                  placeholder="Describe areas where the employee can improve..."
                  maxLength={2000}
                  showCount
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                name="goals"
                label="Goals for Next Period"
              >
                <TextArea
                  rows={4}
                  placeholder="Set goals and objectives for the next review period..."
                  maxLength={2000}
                  showCount
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                name="reviewer_comments"
                label="Reviewer Comments"
              >
                <TextArea
                  rows={4}
                  placeholder="Any additional comments or notes..."
                  maxLength={2000}
                  showCount
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Employee Comments (Read-only if exists) */}
        {reviewData?.employee_comments && (
          <Card title="Employee Comments" style={{ marginBottom: '24px' }}>
            <Text>{reviewData.employee_comments}</Text>
          </Card>
        )}

        {/* Action Buttons */}
        <Row justify="end">
          <Space>
            <Button onClick={() => navigate('/hr/performance-reviews')}>
              Cancel
            </Button>
            <Button
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
              disabled={!isEditable}
            >
              Save as Draft
            </Button>
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSaveAndSubmit}
              loading={saving}
              disabled={!isEditable}
            >
              Save & Submit
            </Button>
          </Space>
        </Row>
      </Form>
    </div>
  );
};

export default PerformanceReviewForm;
